
/* SHY cart popup — Shopify cart data and functionality */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.refreshSequence = 0;

    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close();
    });

    this.querySelector('#CartDrawer-Overlay')
      ?.addEventListener('click', () => this.close());

    this.querySelector('[data-cart-retry]')
      ?.addEventListener('click', () => this.refreshCart());

    this.querySelector('#ShyCartAgree')
      ?.addEventListener('change', (event) => {
        const button = this.querySelector('#ShyCartCheckout');
        if (button) button.disabled = !event.target.checked;
      });

    this.setHeaderCartIconAccessibility();
  }

  get cartUrl() {
    return this.dataset.cartUrl ||
      (window.Shopify?.routes?.root || '/') + 'cart';
  }

  setHeaderCartIconAccessibility() {
    const link = document.querySelector('#cart-icon-bubble');
    if (!link || link.dataset.shyCartBound) return;

    link.dataset.shyCartBound = 'true';
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');

    link.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(link);
    });

    link.addEventListener('keydown', (event) => {
      if (event.code === 'Space') {
        event.preventDefault();
        this.open(link);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');

    this.refreshCart();

    this.querySelector('.drawer__close')
      ?.focus({ preventScroll: true });
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    this.activeElement?.focus?.({ preventScroll: true });
  }

  formatPrice(cents, currency) {
    const amount = Number(cents) / 100;

    return '$' + amount.toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  element(tag, className, text) {
    const el = document.createElement(tag);

    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;

    return el;
  }

  async refreshCart() {
    const sequence = ++this.refreshSequence;

    const loading = this.querySelector('#ShyCartLoading');
    const error = this.querySelector('#ShyCartError');

    if (loading) loading.hidden = false;
    if (error) error.hidden = true;

    try {
      const response = await fetch(this.cartUrl + '.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Cart request failed');
      }

      const cart = await response.json();

      if (sequence !== this.refreshSequence) return;

      this.renderCart(cart);
    } catch (err) {
      if (sequence !== this.refreshSequence) return;
      if (error) error.hidden = false;
    } finally {
      if (sequence === this.refreshSequence && loading) {
        loading.hidden = true;
      }
    }
  }

  renderCart(cart) {
    const list = this.querySelector('#CartDrawer-CartItems');
    const empty = this.querySelector('#ShyCartEmpty');
    const footer = this.querySelector('#ShyCartFooter');
    const heading = this.querySelector('#ShyCartHeading');
    const total = this.querySelector('#ShyCartSubtotal');

    if (!list || !empty || !footer || !heading || !total) {
      return;
    }

    const hasItems = cart.item_count > 0;

    this.classList.toggle('is-empty', !hasItems);

    heading.textContent = `Cart (${cart.item_count})`;

    list.hidden = !hasItems;
    empty.hidden = hasItems;
    footer.hidden = !hasItems;

    // Correct subtotal formatting: $3,990.00 AUD
    total.textContent =
      this.formatPrice(cart.total_price) +
      ' ' +
      (cart.currency || this.dataset.currency || 'AUD');

    list.replaceChildren();

    for (const item of cart.items) {
      const row = this.element('div', 'shy-cart-item');

      if (item.image) {
        const imageLink = this.element('a');
        imageLink.href = item.url;

        const img = this.element(
          'img',
          'shy-cart-item-image'
        );

        img.src = item.image;
        img.alt = item.product_title || item.title;
        img.loading = 'lazy';

        imageLink.append(img);
        row.append(imageLink);
      } else {
        row.append(this.element('div'));
      }

      const info = this.element(
        'div',
        'shy-cart-item-info'
      );

      const price = this.element(
        'div',
        'shy-cart-item-price',
        this.formatPrice(item.final_price)
      );

      info.append(price);

      const title = this.element(
        'a',
        'shy-cart-item-title',
        item.product_title || item.title
      );

      title.href = item.url;
      info.append(title);

      const remove = this.element(
        'button',
        'shy-cart-item-remove',
        'Remove'
      );

      remove.type = 'button';

      remove.setAttribute(
        'aria-label',
        'Remove ' + (item.product_title || item.title)
      );

      remove.addEventListener('click', () => {
        this.removeItem(item.key);
      });

      info.append(remove);
      row.append(info);
      list.append(row);
    }
  }

  async removeItem(key) {
    try {
      const response = await fetch(
        this.cartUrl + '/change.js',
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity: 0
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to remove product');
      }

      await this.refreshCart();
      this.refreshCartBubble();
    } catch (err) {
      const error = this.querySelector('#ShyCartError');
      if (error) error.hidden = false;
    }
  }

  async refreshCartBubble() {
    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(
        root + '?sections=cart-icon-bubble',
        {
          credentials: 'same-origin'
        }
      );

      const sections = await response.json();
      const markup = sections['cart-icon-bubble'];
      const current = document.getElementById(
        'cart-icon-bubble'
      );

      if (markup && current) {
        const fresh = new DOMParser()
          .parseFromString(markup, 'text/html')
          .getElementById('cart-icon-bubble');

        if (fresh) {
          current.innerHTML = fresh.innerHTML;
          this.setHeaderCartIconAccessibility();
        }
      }
    } catch (err) {
      // Cart remains usable if badge refresh fails.
    }
  }

  // Called by Shopify after adding a product.
  renderContents(parsedState) {
    this.productId = parsedState.id;

    const bubble =
      parsedState.sections?.['cart-icon-bubble'];

    if (bubble) {
      const target = document.getElementById(
        'cart-icon-bubble'
      );

      const fresh = new DOMParser()
        .parseFromString(bubble, 'text/html')
        .getElementById('cart-icon-bubble');

      if (target && fresh) {
        target.innerHTML = fresh.innerHTML;
      }
    }

    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
